import { SCRIPT_ROLE_SCORE_RUBRIC_MAP } from "../../shared/scriptRoleScoring";
import type { ScriptRoleScoreKey } from "../../shared/scriptRoleScoring";
import { getScriptContentBeatLabel } from "../../shared/scriptRoleDiversity";

export type ReviewStatus = "writing" | "completed" | "failed" | string;

export interface ReviewListItem {
  reviewId: string;
  status: ReviewStatus;
  sheetTitle?: string | null;
  requestedSheetTitle?: string | null;
  sheetId?: string | null;
  spreadsheetToken?: string | null;
  group?: string | null;
  overwriteMode?: boolean | null;
  reviewStartedAt?: string | null;
  confirmedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  confirmedBy?: ReviewActor | null;
  roleCount?: number | null;
  changedRoleCount?: number | null;
  updatedCells?: number | null;
  attemptCount?: number | null;
  lastError?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ReviewActor {
  id?: string | null;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

export interface ReviewScoreDimension {
  key?: string | null;
  label?: string | null;
  score?: number | null;
  maxScore?: number | null;
  reason?: string | null;
}

export interface ReviewMemberLike {
  wxid?: string | null;
  nickname?: string | null;
  number?: string | null;
  avatar?: string | null;
  group?: string | null;
  category?: string | null;
  categoryCode?: string | null;
  categoryName?: string | null;
  deviceName?: string | null;
  notes?: string | null;
  personaSummary?: string | null;
  compactProfile?: string | null;
  specificHealthTags?: string[] | null;
  specificProductTags?: string[] | null;
  specificFamilyTags?: string[] | null;
  specificStoryTags?: string[] | null;
  specificToneTags?: string[] | null;
  forbiddenTopics?: string[] | null;
}

export interface ReviewAssignmentLike {
  tempRoleId?: string | null;
  wxid?: string | null;
  number?: string | null;
  nickname?: string | null;
  reason?: string | null;
  score?: number | null;
  scoreBreakdown?: ReviewScoreDimension[] | null;
  selectionSource?: string | null;
  originalWxid?: string | null;
  selectedMember?: ReviewMemberLike | null;
  changed?: boolean | null;
  matchedRanking?: unknown;
  matchedRankingPosition?: number | null;
}

export interface ReviewTempRoleLike {
  tempRoleId?: string | null;
  speakerLabel?: string | null;
  utteranceCount?: number | null;
  contentBeat?: string | null;
  firstRowIndex?: number | null;
  lastRowIndex?: number | null;
  contentRowNumbers?: number[] | null;
  fullText?: string | null;
}

export interface ReviewPersonaLike {
  tempRoleId?: string | null;
  personaSummary?: string | null;
  toneTags?: string[] | null;
  topicTags?: string[] | null;
  sampleQuotes?: string[] | null;
}

export interface ReviewScriptRowLike {
  rowIndex?: number | null;
  rowNumber?: number | null;
  role?: string | null;
  roleIsInherited?: boolean | null;
  cells?: Record<string, unknown> | null;
  cellImages?: Record<string, unknown> | null;
  roleImageFileToken?: string | null;
}

export interface ReviewChangeLike {
  tempRoleId?: string | null;
  fromWxid?: string | null;
  toWxid?: string | null;
  changedAt?: string | null;
  changedBy?: ReviewActor | null;
}

export interface ReviewWriteAttemptLike {
  attemptId?: string | null;
  status?: string | null;
  actor?: ReviewActor | null;
  startedAt?: string | null;
  completedAt?: string | null;
  error?: string | null;
  updatedCells?: number | null;
}

export interface ReviewDataCoverageLike {
  sourceSnapshotComplete?: boolean | null;
  imageTextsComplete?: boolean | null;
  personasComplete?: boolean | null;
  memberSnapshotsComplete?: boolean | null;
  matchingTimeMemberSnapshotsExact?: boolean | null;
  memberSnapshotCoverageReason?: string | null;
  executionImageTextsExact?: boolean | null;
  imageTextCoverageReason?: string | null;
  matchJobAvailable?: boolean | null;
  matchRankingsAvailable?: boolean | null;
  matchRankingsComplete?: boolean | null;
  missingMatchRankingTempRoleIds?: string[] | null;
  executionModelExact?: boolean | null;
  executionModelCoverageReason?: string | null;
  estimatedBsonBytes?: number | null;
  storageWithinMongoLimit?: boolean | null;
}

export interface ReviewDetailRecord {
  reviewId?: string | null;
  status?: ReviewStatus | null;
  sheetTitle?: string | null;
  requestedSheetTitle?: string | null;
  sheetId?: string | null;
  spreadsheetToken?: string | null;
  group?: string | null;
  overwriteMode?: boolean | null;
  reviewStartedAt?: string | null;
  confirmedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  confirmedBy?: ReviewActor | null;
  lastError?: string | null;
  attemptCount?: number | null;
  sourceSnapshot?: {
    range?: string | null;
    rowCount?: number | null;
    columnCount?: number | null;
    scriptRows?: ReviewScriptRowLike[] | null;
    matchingRows?: ReviewScriptRowLike[] | null;
  } | null;
  imageTextsByFileToken?: Record<string, string> | null;
  tempRoles?: ReviewTempRoleLike[] | null;
  personas?: Record<string, ReviewPersonaLike> | ReviewPersonaLike[] | null;
  originalAssignments?: ReviewAssignmentLike[] | null;
  finalAssignments?: ReviewAssignmentLike[] | null;
  memberSnapshots?: ReviewMemberLike[] | null;
  submittedChangeHistory?: ReviewChangeLike[] | null;
  changes?: ReviewChangeLike[] | null;
  writeAttempts?: ReviewWriteAttemptLike[] | null;
  dataCoverage?: ReviewDataCoverageLike | null;
  roleCount?: number | null;
  changedRoleCount?: number | null;
  updatedCells?: number | null;
  writeResult?: { updatedCells?: number | null } | null;
  writePlan?: { replacements?: Array<{ rowIndex?: number | null; value?: string | null }> | null } | null;
  [key: string]: unknown;
}

export interface ReviewRoleScriptLine {
  rowIndex: number;
  rowNumber: number;
  role: string;
  text: string;
  imageToken: string;
  imageUnderstanding: string;
  columnD: string;
}

export interface ReviewRoleDetailView {
  tempRoleId: string;
  speakerLabel: string;
  contentBeatLabel: string;
  utteranceCount: number;
  changed: boolean;
  scriptLines: ReviewRoleScriptLine[];
  personaSummary: string;
  toneTags: string[];
  topicTags: string[];
  sampleQuotes: string[];
  originalAssignment: {
    wxid: string;
    number: string;
    nickname: string;
    score: number | null;
    reason: string;
    scoreBreakdown: Array<{
      key: string;
      label: string;
      score: number;
      maxScore: number | null;
      reason: string;
    }>;
  } | null;
  finalMember: {
    wxid: string;
    number: string;
    nickname: string;
    category: string;
    deviceName: string;
    personaSummary: string;
    compactProfile: string;
    notes: string;
    tags: string[];
  } | null;
}

export interface ReviewMemberLabel {
  wxid: string;
  number: string;
  nickname: string;
  label: string;
}

const EMPTY_TEXT = "—";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return fallback;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function asStringArray(value: unknown): string[] {
  return asArray(value)
    .map((item) => asString(item).trim())
    .filter(Boolean);
}

export function resolveClampedListPage(input: {
  page: number;
  pageSize: number;
  total: number;
  itemCount: number;
}): number | null {
  const page = Math.max(1, Math.floor(Number(input.page) || 1));
  const pageSize = Math.max(1, Math.floor(Number(input.pageSize) || 1));
  const total = Math.max(0, Math.floor(Number(input.total) || 0));
  const itemCount = Math.max(0, Math.floor(Number(input.itemCount) || 0));

  if (total <= 0) {
    return page === 1 ? null : 1;
  }
  if (page > 1 && itemCount === 0) {
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    return page === lastPage ? null : lastPage;
  }
  return null;
}

/** Display null/undefined counts as em dash; keep real zero as 0. */
export function formatNullableCount(value: unknown): string | number {
  const n = asNumber(value);
  return n == null ? EMPTY_TEXT : n;
}

export function formatReviewDateTime(value: unknown): string {
  const raw = asString(value).trim();
  if (!raw) return EMPTY_TEXT;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("zh-CN", { hour12: false });
}

export function resolveReviewSheetTitle(item: {
  sheetTitle?: string | null;
  requestedSheetTitle?: string | null;
  sheetId?: string | null;
  spreadsheetToken?: string | null;
}): string {
  const title = asString(item.sheetTitle).trim();
  if (title) return title;
  const requested = asString(item.requestedSheetTitle).trim();
  if (requested) return requested;
  const sheetId = asString(item.sheetId).trim();
  if (sheetId) return sheetId;
  const token = asString(item.spreadsheetToken).trim();
  if (token) return token;
  return EMPTY_TEXT;
}

export function formatReviewActor(actor: ReviewActor | null | undefined): string {
  if (!actor) return EMPTY_TEXT;
  const name = asString(actor.name).trim();
  if (name) return name;
  const email = asString(actor.email).trim();
  if (email) return email;
  const id = asString(actor.id).trim();
  return id || EMPTY_TEXT;
}

export function getReviewStatusMeta(status: unknown): {
  label: string;
  color: "default" | "processing" | "success" | "error" | "warning";
} {
  switch (asString(status)) {
    case "completed":
      return { label: "已完成", color: "success" };
    case "writing":
      return { label: "写入中", color: "processing" };
    case "failed":
      return { label: "失败", color: "error" };
    default:
      return { label: asString(status, "未知"), color: "default" };
  }
}

export function formatOverwriteMode(value: unknown): string {
  return asBoolean(value) ? "覆盖写入" : "仅空位写入";
}

export function resolveMemberLabel(
  wxid: unknown,
  memberByWxid: Map<string, ReviewMemberLabel>
): ReviewMemberLabel {
  const normalized = asString(wxid).trim();
  if (!normalized) {
    return {
      wxid: "",
      number: "",
      nickname: "",
      label: EMPTY_TEXT,
    };
  }
  const found = memberByWxid.get(normalized);
  if (found) return found;
  return {
    wxid: normalized,
    number: "",
    nickname: "",
    label: normalized,
  };
}

function buildMemberLabel(member: ReviewMemberLike | null | undefined, fallbackWxid = ""): ReviewMemberLabel {
  const wxid = asString(member?.wxid || fallbackWxid).trim();
  const number = asString(member?.number).trim();
  const nickname = asString(member?.nickname).trim();
  const parts = [number || "", nickname || ""].filter(Boolean);
  return {
    wxid,
    number,
    nickname,
    label: parts.length > 0 ? parts.join(" ") : wxid || EMPTY_TEXT,
  };
}

export function buildMemberLabelMap(detail: ReviewDetailRecord | null | undefined): Map<string, ReviewMemberLabel> {
  const map = new Map<string, ReviewMemberLabel>();
  const upsert = (member: ReviewMemberLike | null | undefined, fallbackWxid = "") => {
    const label = buildMemberLabel(member, fallbackWxid);
    if (!label.wxid) return;
    const existing = map.get(label.wxid);
    if (!existing || existing.label === existing.wxid) {
      map.set(label.wxid, label);
    }
  };

  for (const member of asArray<ReviewMemberLike>(detail?.memberSnapshots)) {
    upsert(member);
  }
  for (const assignment of asArray<ReviewAssignmentLike>(detail?.finalAssignments)) {
    upsert(assignment.selectedMember, asString(assignment.selectedMember?.wxid || assignment.wxid));
    const originalWxid = asString(assignment.originalWxid).trim();
    if (originalWxid && !map.has(originalWxid)) {
      upsert({ wxid: originalWxid }, originalWxid);
    }
  }
  for (const assignment of asArray<ReviewAssignmentLike>(detail?.originalAssignments)) {
    upsert(
      {
        wxid: assignment.wxid,
        number: assignment.number,
        nickname: assignment.nickname,
      },
      asString(assignment.wxid)
    );
  }
  return map;
}

function normalizePersonaMap(
  personas: ReviewDetailRecord["personas"]
): Map<string, ReviewPersonaLike> {
  const map = new Map<string, ReviewPersonaLike>();
  if (Array.isArray(personas)) {
    for (const persona of personas) {
      const tempRoleId = asString(persona?.tempRoleId).trim();
      if (tempRoleId) map.set(tempRoleId, persona);
    }
    return map;
  }
  const record = asRecord(personas);
  if (!record) return map;
  for (const [tempRoleId, persona] of Object.entries(record)) {
    const normalizedId = asString(tempRoleId).trim();
    if (!normalizedId) continue;
    if (persona && typeof persona === "object") {
      map.set(normalizedId, persona as ReviewPersonaLike);
    }
  }
  return map;
}

function cellText(cells: Record<string, unknown> | null | undefined, key: string): string {
  if (!cells) return "";
  return asString(cells[key]).trim();
}

function cellImageToken(
  cellImages: Record<string, unknown> | null | undefined,
  key: string
): string {
  if (!cellImages) return "";
  return asString(cellImages[key]).trim();
}

function collectRowsForRole(
  tempRoleId: string,
  tempRole: ReviewTempRoleLike | undefined,
  scriptRows: ReviewScriptRowLike[],
  matchingRows: ReviewScriptRowLike[],
  imageTextsByFileToken: Record<string, string>
): ReviewRoleScriptLine[] {
  const preferredRows = matchingRows.length > 0 ? matchingRows : scriptRows;
  const contentRowNumbers = new Set(
    asArray<number>(tempRole?.contentRowNumbers)
      .map((value) => asNumber(value))
      .filter((value): value is number => value != null)
  );
  const firstRowIndex = asNumber(tempRole?.firstRowIndex);
  const lastRowIndex = asNumber(tempRole?.lastRowIndex) ?? firstRowIndex;

  let selected = preferredRows.filter((row) => {
    const rowNumber = asNumber(row.rowNumber);
    if (contentRowNumbers.size > 0 && rowNumber != null && contentRowNumbers.has(rowNumber)) {
      return true;
    }
    return false;
  });

  if (selected.length === 0 && firstRowIndex != null && lastRowIndex != null) {
    selected = preferredRows.filter((row) => {
      const rowIndex = asNumber(row.rowIndex);
      return rowIndex != null && rowIndex >= firstRowIndex && rowIndex <= lastRowIndex;
    });
  }

  if (selected.length === 0) {
    selected = preferredRows.filter((row) => {
      const role = asString(row.role);
      return role.includes(tempRoleId);
    });
  }

  return selected
    .map((row) => {
      const rowIndex = asNumber(row.rowIndex) ?? 0;
      const rowNumber = asNumber(row.rowNumber) ?? rowIndex + 1;
      const imageToken = cellImageToken(row.cellImages, "C");
      return {
        rowIndex,
        rowNumber,
        role: asString(row.role, "气氛员"),
        text: cellText(row.cells, "C"),
        imageToken,
        imageUnderstanding: asString(imageTextsByFileToken[imageToken]).trim(),
        columnD: cellText(row.cells, "D") || "空",
      };
    })
    .sort((left, right) => left.rowIndex - right.rowIndex);
}

function normalizeScoreBreakdown(
  breakdown: ReviewScoreDimension[] | null | undefined
): Array<{
  key: string;
  label: string;
  score: number;
  maxScore: number | null;
  reason: string;
}> {
  return asArray<ReviewScoreDimension>(breakdown).map((item, index) => {
    const key = asString(item?.key, `dimension-${index}`);
    const rubric = SCRIPT_ROLE_SCORE_RUBRIC_MAP[key as ScriptRoleScoreKey];
    return {
      key,
      label: asString(item?.label) || rubric?.label || key,
      score: asNumber(item?.score) ?? 0,
      maxScore: asNumber(item?.maxScore) ?? rubric?.maxScore ?? null,
      reason: asString(item?.reason),
    };
  });
}

function buildFinalMemberView(
  finalAssignment: ReviewAssignmentLike | undefined,
  memberByWxid: Map<string, ReviewMemberLabel>,
  memberSnapshots: ReviewMemberLike[]
): ReviewRoleDetailView["finalMember"] {
  const selected = finalAssignment?.selectedMember;
  const wxid = asString(selected?.wxid || finalAssignment?.wxid).trim();
  const snapshot =
    selected ||
    memberSnapshots.find((member) => asString(member.wxid).trim() === wxid) ||
    null;
  if (!snapshot && !wxid) return null;

  const label = resolveMemberLabel(wxid, memberByWxid);
  const categoryParts = [
    asString(snapshot?.categoryCode).trim(),
    asString(snapshot?.categoryName || snapshot?.category).trim(),
  ].filter(Boolean);
  const tags = [
    ...asStringArray(snapshot?.specificHealthTags),
    ...asStringArray(snapshot?.specificProductTags),
    ...asStringArray(snapshot?.specificFamilyTags),
    ...asStringArray(snapshot?.specificStoryTags),
    ...asStringArray(snapshot?.specificToneTags),
    ...asStringArray(snapshot?.forbiddenTopics),
  ];

  return {
    wxid: wxid || label.wxid,
    number: asString(snapshot?.number) || label.number,
    nickname: asString(snapshot?.nickname) || label.nickname,
    category: categoryParts.join("-") || "未分类",
    deviceName: asString(snapshot?.deviceName) || "未填写设备",
    personaSummary: asString(snapshot?.personaSummary),
    compactProfile: asString(snapshot?.compactProfile),
    notes: asString(snapshot?.notes),
    tags,
  };
}

export function buildRoleDetailViews(
  detail: ReviewDetailRecord | null | undefined,
  options?: { onlyChanged?: boolean }
): ReviewRoleDetailView[] {
  if (!detail) return [];

  const tempRoles = asArray<ReviewTempRoleLike>(detail.tempRoles);
  const originalAssignments = asArray<ReviewAssignmentLike>(detail.originalAssignments);
  const finalAssignments = asArray<ReviewAssignmentLike>(detail.finalAssignments);
  const memberSnapshots = asArray<ReviewMemberLike>(detail.memberSnapshots);
  const scriptRows = asArray<ReviewScriptRowLike>(detail.sourceSnapshot?.scriptRows);
  const matchingRows = asArray<ReviewScriptRowLike>(detail.sourceSnapshot?.matchingRows);
  const imageTextsByFileToken = Object.fromEntries(
    Object.entries(asRecord(detail.imageTextsByFileToken) ?? {}).map(([token, text]) => [
      token,
      asString(text),
    ])
  );
  const personaMap = normalizePersonaMap(detail.personas);
  const memberByWxid = buildMemberLabelMap(detail);
  const originalByRole = new Map(
    originalAssignments
      .map((item) => [asString(item.tempRoleId).trim(), item] as const)
      .filter(([tempRoleId]) => Boolean(tempRoleId))
  );
  const finalByRole = new Map(
    finalAssignments
      .map((item) => [asString(item.tempRoleId).trim(), item] as const)
      .filter(([tempRoleId]) => Boolean(tempRoleId))
  );

  const roleIds = new Set<string>();
  for (const role of tempRoles) {
    const id = asString(role.tempRoleId).trim();
    if (id) roleIds.add(id);
  }
  for (const id of originalByRole.keys()) roleIds.add(id);
  for (const id of finalByRole.keys()) roleIds.add(id);
  for (const id of personaMap.keys()) roleIds.add(id);

  const roleMetaById = new Map(
    tempRoles
      .map((role) => [asString(role.tempRoleId).trim(), role] as const)
      .filter(([tempRoleId]) => Boolean(tempRoleId))
  );

  const views = [...roleIds]
    .sort((left, right) => {
      const leftIndex = asNumber(roleMetaById.get(left)?.firstRowIndex) ?? Number.MAX_SAFE_INTEGER;
      const rightIndex = asNumber(roleMetaById.get(right)?.firstRowIndex) ?? Number.MAX_SAFE_INTEGER;
      if (leftIndex !== rightIndex) return leftIndex - rightIndex;
      return left.localeCompare(right, "zh-CN");
    })
    .map((tempRoleId) => {
      const tempRole = roleMetaById.get(tempRoleId);
      const original = originalByRole.get(tempRoleId);
      const finalAssignment = finalByRole.get(tempRoleId);
      const persona = personaMap.get(tempRoleId);
      const finalWxid = asString(
        finalAssignment?.selectedMember?.wxid || finalAssignment?.wxid
      ).trim();
      const originalWxid = asString(original?.wxid || finalAssignment?.originalWxid).trim();
      const changed =
        asBoolean(finalAssignment?.changed) ||
        (Boolean(originalWxid) && Boolean(finalWxid) && originalWxid !== finalWxid);

      return {
        tempRoleId,
        speakerLabel: asString(tempRole?.speakerLabel) || `气氛员${tempRoleId}`,
        contentBeatLabel: (() => {
          const beat = asString(tempRole?.contentBeat) || "general";
          try {
            return getScriptContentBeatLabel(beat as never) || "综合";
          } catch {
            return "综合";
          }
        })(),
        utteranceCount:
          asNumber(tempRole?.utteranceCount) ??
          asArray(tempRole?.contentRowNumbers).length,
        changed,
        scriptLines: collectRowsForRole(
          tempRoleId,
          tempRole,
          scriptRows,
          matchingRows,
          imageTextsByFileToken
        ),
        personaSummary: asString(persona?.personaSummary),
        toneTags: asStringArray(persona?.toneTags),
        topicTags: asStringArray(persona?.topicTags),
        sampleQuotes: asStringArray(persona?.sampleQuotes),
        originalAssignment: original
          ? {
              wxid: asString(original.wxid),
              number: asString(original.number),
              nickname: asString(original.nickname),
              score: asNumber(original.score),
              reason: asString(original.reason),
              scoreBreakdown: normalizeScoreBreakdown(original.scoreBreakdown),
            }
          : null,
        finalMember: buildFinalMemberView(finalAssignment, memberByWxid, memberSnapshots),
      } satisfies ReviewRoleDetailView;
    });

  if (options?.onlyChanged) {
    return views.filter((item) => item.changed);
  }
  return views;
}

export function summarizeReviewDetail(detail: ReviewDetailRecord | null | undefined) {
  const roleViews = buildRoleDetailViews(detail);
  const roleCount = asNumber(detail?.roleCount) ?? roleViews.length;
  const changedRoleCount =
    asNumber(detail?.changedRoleCount) ?? roleViews.filter((item) => item.changed).length;
  const lastAttempt = asArray<ReviewWriteAttemptLike>(detail?.writeAttempts).at(-1);
  const updatedCells =
    asNumber(detail?.updatedCells) ??
    asNumber(detail?.writeResult?.updatedCells) ??
    asNumber(lastAttempt?.updatedCells) ??
    asArray(detail?.writePlan?.replacements).length;

  return {
    roleCount,
    changedRoleCount,
    updatedCells,
  };
}

export function mapChangeRows(
  changes: ReviewChangeLike[] | null | undefined,
  memberByWxid: Map<string, ReviewMemberLabel>
) {
  return asArray<ReviewChangeLike>(changes).map((change, index) => {
    const from = resolveMemberLabel(change.fromWxid, memberByWxid);
    const to = resolveMemberLabel(change.toWxid, memberByWxid);
    return {
      key: `${asString(change.tempRoleId)}-${asString(change.changedAt)}-${index}`,
      tempRoleId: asString(change.tempRoleId) || EMPTY_TEXT,
      fromLabel: from.label,
      toLabel: to.label,
      fromWxid: from.wxid || asString(change.fromWxid) || EMPTY_TEXT,
      toWxid: to.wxid || asString(change.toWxid) || EMPTY_TEXT,
      changedAt: formatReviewDateTime(change.changedAt),
      changedBy: formatReviewActor(change.changedBy),
    };
  });
}

export function mapWriteAttemptRows(attempts: ReviewWriteAttemptLike[] | null | undefined) {
  return asArray<ReviewWriteAttemptLike>(attempts).map((attempt, index) => ({
    key: asString(attempt.attemptId, `attempt-${index}`),
    attemptId: asString(attempt.attemptId, `attempt-${index}`),
    status: asString(attempt.status, "unknown"),
    actor: formatReviewActor(attempt.actor),
    startedAt: formatReviewDateTime(attempt.startedAt),
    completedAt: formatReviewDateTime(attempt.completedAt),
    updatedCells: asNumber(attempt.updatedCells) ?? 0,
    error: asString(attempt.error),
  }));
}

export function mapDataCoverageItems(coverage: ReviewDataCoverageLike | null | undefined) {
  if (!coverage) return [];
  const boolLabel = (value: unknown) => (value === true ? "完整" : value === false ? "缺失" : "未知");
  return [
    { key: "sourceSnapshotComplete", label: "剧本快照", value: boolLabel(coverage.sourceSnapshotComplete) },
    { key: "imageTextsComplete", label: "图片理解", value: boolLabel(coverage.imageTextsComplete) },
    { key: "personasComplete", label: "角色画像", value: boolLabel(coverage.personasComplete) },
    {
      key: "memberSnapshotsComplete",
      label: "成员快照",
      value: boolLabel(coverage.memberSnapshotsComplete),
    },
    {
      key: "matchingTimeMemberSnapshotsExact",
      label: "匹配时成员池精确",
      value: boolLabel(coverage.matchingTimeMemberSnapshotsExact),
      extra: asString(coverage.memberSnapshotCoverageReason),
    },
    {
      key: "executionImageTextsExact",
      label: "执行图片理解精确",
      value: boolLabel(coverage.executionImageTextsExact),
      extra: asString(coverage.imageTextCoverageReason),
    },
    { key: "matchJobAvailable", label: "匹配任务快照", value: boolLabel(coverage.matchJobAvailable) },
    {
      key: "matchRankingsAvailable",
      label: "匹配排序可用",
      value: boolLabel(coverage.matchRankingsAvailable),
    },
    {
      key: "matchRankingsComplete",
      label: "匹配排序完整",
      value: boolLabel(coverage.matchRankingsComplete),
      extra:
        asStringArray(coverage.missingMatchRankingTempRoleIds).length > 0
          ? `缺失角色：${asStringArray(coverage.missingMatchRankingTempRoleIds).join("、")}`
          : "",
    },
    {
      key: "executionModelExact",
      label: "执行模型精确",
      value: boolLabel(coverage.executionModelExact),
      extra: asString(coverage.executionModelCoverageReason),
    },
    {
      key: "storageWithinMongoLimit",
      label: "存储体积",
      value:
        coverage.storageWithinMongoLimit === true
          ? "安全"
          : coverage.storageWithinMongoLimit === false
            ? "超限"
            : "未知",
      extra:
        asNumber(coverage.estimatedBsonBytes) != null
          ? `${asNumber(coverage.estimatedBsonBytes)} 字节`
          : "",
    },
  ];
}
